import React from 'react';
import { X, User, Calendar, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { ResponseWithAnswersDto } from '../../services/feedbackService';

interface ResponseDetailModalProps {
  response: ResponseWithAnswersDto;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalCount?: number;
}

const ResponseDetailModal: React.FC<ResponseDetailModalProps> = ({
  response,
  onClose,
  onPrevious,
  onNext,
  currentIndex,
  totalCount,
}) => {
  const formatAnswerValue = (value: any, questionType: string, displayValue?: string): string => {
    if (displayValue) return displayValue;

    if (value === null || value === undefined) return 'No answer provided';

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'string') {
      if (questionType === 'rating') return `${value} / 5`;
      return value;
    }

    if (typeof value === 'number') {
      if (questionType === 'rating') return `${value} / 5`;
      return String(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
    }

    if (typeof value === 'object') {
      if ('text' in value && typeof value.text === 'string') return value.text;
      if ('rating' in value) return `${value.rating} / 5`;
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Response Details
            </h2>
            {currentIndex !== undefined && totalCount !== undefined && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {currentIndex + 1} of {totalCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onPrevious && (
              <button
                onClick={onPrevious}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {onNext && (
              <button
                onClick={onNext}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                disabled={currentIndex === (totalCount || 0) - 1}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              {response.respondent && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">Respondent:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {response.respondent.fullName}
                  </span>
                </div>
              )}
              {response.submittedAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">Submitted:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date(response.submittedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600 dark:text-gray-400">Version:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  v{response.versionNo}
                </span>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {response.answers.map((answer, index) => (
              <div
                key={answer.id}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${answer.isFlagged ? 'border-l-4 border-l-red-500' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {answer.questionTitle || answer.questionName}
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3">
                      <pre className="whitespace-pre-wrap font-sans">
                        {formatAnswerValue(answer.value, answer.questionType, answer.displayValue)}
                      </pre>
                    </div>
                    {answer.isFlagged && (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 mt-1">
                        🚩 {answer.flagLabel || 'Flagged'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {response.answers.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-gray-600 dark:text-gray-400">No answers recorded</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResponseDetailModal;
